<template>
  <div class="panel">
    <header>
      <vs-button
        class="vs-asidePanel"
        icon="chevron_left"
        label="Locations"
        small
        data-test="hide-filter"
        @click="handleLeavePanel"
      />

      <chip-filter
        class="pt-2"
        :items="locationTypes"
        :value="locationType"
        @update="selectLocationType"
      />

      <AutoCompleteShell
        ref="autocomplete"
        label=""
        placeholder="Look for..."
        small-chips
        keep-value-on-clicking-outside
        @update:search="updateSearch"
        @clear="clear"
      >
        <template #append>
          <v-icon> search </v-icon>
        </template>
      </AutoCompleteShell>

      <div v-if="internalSelection.length > 0" class="result-toolbar">
        <vs-text class="item-counter" type="subdued" center>
          {{ internalSelection.length }} selected
        </vs-text>
        <v-tooltip bottom max-width="240">
          <template #activator="{ on }">
            <v-icon small v-on="on">info</v-icon>
          </template>
          Search results will include all locations in the hierarchy if a parent location is
          selected
        </v-tooltip>
        <v-btn icon x-small @click="clear">
          <v-icon small color="red">mdi-close</v-icon>
        </v-btn>
      </div>
    </header>

    <div class="header" v-if="parentName">
      <vs-button
        small
        type="secondary"
        icon="mdi-chevron-left"
        :label="parentName"
        @click.stop="navigateBack"
      />
    </div>
    <picker-list-virtualized ref="virtualizedList" :items="items" @near-bottom="continueFetching">
      <template #before>
        <picker-list-item
          v-if="showNoLocationOption"
          checkbox
          :item="noLocationOption"
          :is-selected="isSelected(noLocationOption)"
          @select="selectItem"
        />
      </template>
      <template #item="{ item }">
        <picker-list-item
          checkbox
          :item="item"
          :is-selected="isSelected(item)"
          @expand="navigateIntoItem"
          @select="selectItem"
        />
      </template>
      <template #after>
        <v-container v-if="isFetching" class="my-2">
          <v-row justify="center">
            <vs-text center>loading...</vs-text>
          </v-row>
        </v-container>
      </template>
    </picker-list-virtualized>
    <div class="no-data" v-if="noResultsMessage">
      <v-container class="my-2">
        <v-row justify="center">
          <vs-text center>{{ noResultsMessage }}</vs-text>
        </v-row>
      </v-container>
    </div>

    <v-container v-if="isDirty" class="panel-footer">
      <v-row>
        <v-col cols="6">
          <vs-button full-width label="Cancel" small type="secondary" @click="reset" />
        </v-col>
        <v-col cols="6">
          <vs-button full-width label="Save" small type="primary" @click="save" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import AutoCompleteShell from '@/components/vision/next/inputs/AutoCompleteShell.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import PickerListItem from '@/components/vision/next/picker-list/PickerListItem.vue'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import { useHeadlessLocationPicker } from '@/components/locations/HeadlessLocationPicker'
import { computed, nextTick, ref, toRef, onMounted } from 'vue'
import { HeadlessAutoCompleteValueType } from '@/components/vision/next/inputs/useAutoComplete'
import { useQueryClient } from '@tanstack/vue-query'
import { getById } from '@/api/locations'
import moment from 'moment'
import { isEqual } from 'lodash'

const props = withDefaults(
  defineProps<{
    selectedItems: number[]
  }>(),
  {
    selectedItems: () => [],
  },
)

const emit = defineEmits<{
  (event: 'leave-panel'): void
  (event: 'save-filters', locationIds: number[]): void
}>()

const internalSelection = ref<number[]>(props.selectedItems)

const autocomplete = ref<InstanceType<typeof AutoCompleteShell>>()
const virtualizedList = ref<InstanceType<typeof PickerListVirtualized>>()

const noLocationOption = {
  id: null,
  title: 'Let the dispatcher decide',
  subTitle: 'Show jobs with no specified pickup location',
  selectable: true,
}

const getScrollPosition = () => {
  return virtualizedList?.value?.getScrollPosition()
}

const scrollToPosition = (pos: number) => {
  return virtualizedList?.value?.scrollToPosition(pos)
}

const onNavigate = () => {
  autocomplete.value?.input?.focus()
}

const onValueChanged = (items: HeadlessAutoCompleteValueType[]) => {
  autocomplete.value?.input?.focus()
  internalSelection.value = items.map((i) => i.id as number)
}

const isSelected = (item: HeadlessAutoCompleteValueType) => {
  return internalSelection.value.includes(item.id as number)
}

const client = useQueryClient()

const valueResolver = async (id) => {
  if (id === null) {
    return noLocationOption
  }

  const location = await client.fetchQuery({
    queryKey: ['locations', id],
    queryFn: ({ signal }) => getById(id, signal),
    staleTime: moment.duration(10, 'minutes').asMilliseconds(),
  })

  return { id: location.id, title: location.location }
}

const {
  items,
  isFetching,
  locationType,
  locationTypes,
  selectLocationType,
  parentName,
  updateSearch,
  continueFetching,
  navigateIntoItem,
  navigateBack,
  selectItem,
  noResultsMessage,
  clear,
  rawSearchText,
} = useHeadlessLocationPicker({
  getScrollPosition,
  scrollToPosition,
  onNavigate,
  onValueChanged,
  multiple: true,
  value: internalSelection,
  valueResolver,
})

const isDirty = computed(() => !isEqual(internalSelection.value, props.selectedItems))

const showNoLocationOption = computed(() => {
  return !locationType.value?.id && !parentName.value && !rawSearchText.value
})

const reset = () => {
  internalSelection.value = []
  clear()
  handleLeavePanel()
}

const save = () => {
  emit('save-filters', internalSelection.value)
}

const handleLeavePanel = () => {
  emit('leave-panel')
}

onMounted(() => {
  autocomplete.value?.input?.focus()
})
</script>

<style scoped>
.panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  border-right: 1px solid var(--color-grey--lightest);
  overflow: hidden;
}

.panel > div {
  width: 100%;
  overflow: hidden;
}

header {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: var(--space-small);
  border-bottom: var(--color-grey--lightest) solid 2px;
  box-shadow: 0 6px 6px -6px var(--color-grey--lighter);
  padding: var(--space-small);
}

.header {
  padding: var(--space-small);
}

.field-wrapper {
  align-self: stretch;
  border: 1px solid var(--color-grey--lighter);
  border-radius: 24px;
  overflow: hidden;
  transition: all 200ms;
}

.field-wrapper:focus-within,
.field-wrapper:hover {
  border-color: var(--color-blue);
}

.field-suffix v-icon {
  color: var(--color-grey--lighter);
}

.field {
  display: flex;
  padding: 0 var(--space-base);
  align-items: center;
}

.chip-container {
  display: flex;
  flex-wrap: wrap;
  max-height: 130px;
  overflow-y: auto;
  padding: calc(var(--space-base) - 4px) calc(var(--space-base) - 4px) 0;
  gap: var(--space-smaller);
}

.chip-container:empty {
  display: none;
}

.search-input {
  width: 100%;
  line-height: 20px;
  padding: calc(var(--space-base) - 2px) 0;
}

.search-input:focus {
  outline: none;
}

.result-toolbar {
  display: inline-flex;
  align-items: center;
  align-self: center;
  gap: var(--space-smaller);
}

.vs-asidePanel {
  --text-color: var(--color-grey--dark);
  background-color: transparent;
  font-size: 14px;
}

.vs-asidePanel:hover,
.vs-asidePanel:focus {
  background-color: var(--color-grey--lightest);
}

:deep(.field-wrapper) {
  max-height: 400px;
  overflow-y: auto;
}
</style>
